import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Pencil, Save, X, Tag, Power, PowerOff } from "lucide-react";

const emptyForm = () => ({
  code: "", discount_type: "percent", discount_value: 10,
  applies_to: "all", target_ids: [], usage_limit: "",
  valid_from: "", valid_until: "", is_active: true, min_amount: 0, description: "",
});

export default function AdminPromo() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | code
  const [form, setForm] = useState(emptyForm());

  const load = () => api.get("/promo").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm(emptyForm()); };
  const startEdit = (p) => {
    setEditing(p.code);
    setForm({
      code: p.code, discount_type: p.discount_type, discount_value: p.discount_value,
      applies_to: p.applies_to || "all", target_ids: p.target_ids || [],
      usage_limit: p.usage_limit ?? "", valid_from: p.valid_from?.slice(0, 16) || "",
      valid_until: p.valid_until?.slice(0, 16) || "",
      is_active: p.is_active !== false, min_amount: p.min_amount || 0,
      description: p.description || "",
    });
  };
  const cancel = () => { setEditing(null); setForm(emptyForm()); };

  const save = async () => {
    const body = {
      ...form,
      discount_value: parseFloat(form.discount_value) || 0,
      min_amount: parseFloat(form.min_amount) || 0,
      usage_limit: form.usage_limit !== "" ? parseInt(form.usage_limit) : null,
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
    };
    if (editing === "new") {
      body.code = form.code.toUpperCase().trim();
      await api.post("/promo", body);
    } else {
      delete body.code;
      await api.patch(`/promo/${editing}`, body);
    }
    cancel(); load();
  };

  const remove = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    await api.delete(`/promo/${code}`);
    await load();
    window.location.reload();
  };

  const toggleActive = async (p) => {
    await api.patch(`/promo/${p.code}`, { is_active: !p.is_active });
    load();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="admin-promo-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <p className="label">Admin</p>
          <h1 className="font-heading text-3xl font-black flex items-center gap-2"><Tag className="w-7 h-7 text-brand-green" /> Codes promo</h1>
        </div>
        {!editing && <button onClick={startCreate} className="btn-primary w-full sm:w-auto justify-center" data-testid="admin-create-promo-btn"><Plus className="w-4 h-4" /> Nouveau code</button>}
      </div>

      {editing && (
        <div className="card p-6 mb-6 space-y-4" data-testid="admin-promo-form">
          <h2 className="font-heading text-xl font-bold">{editing === "new" ? "Créer un code promo" : `Éditer ${editing}`}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label">Code (en MAJUSCULES)</label>
              <input className="input uppercase font-mono" value={form.code} disabled={editing !== "new"} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="BLACKFRIDAY" data-testid="promo-code-input" />
            </div>
            <div>
              <label className="label">Description (interne)</label>
              <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Lancement Black Friday" />
            </div>
            <div>
              <label className="label">Type de remise</label>
              <select className="input" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="promo-discount-type-select">
                <option value="percent">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (€)</option>
              </select>
            </div>
            <div>
              <label className="label">Valeur</label>
              <input className="input" type="number" step="0.01" min="0" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="promo-discount-value-input" />
            </div>
            <div>
              <label className="label">Applicable à</label>
              <select className="input" value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })} data-testid="promo-applies-to-select">
                <option value="all">Tout (formations + packs)</option>
                <option value="courses">Formations seulement</option>
                <option value="packs">Packs seulement</option>
              </select>
            </div>
            <div>
              <label className="label">Limite d'utilisations (vide = illimité)</label>
              <input className="input" type="number" min="0" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="100" />
            </div>
            <div>
              <label className="label">Montant minimum (€)</label>
              <input className="input" type="number" step="0.01" min="0" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} />
            </div>
            <div>
              <label className="label">Valide jusqu'au</label>
              <input className="input" type="datetime-local" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            </div>
          </div>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Code actif</label>
          <div className="flex gap-2">
            <button onClick={save} disabled={!form.code || !form.discount_value} className="btn-primary" data-testid="promo-save-btn"><Save className="w-4 h-4" /> Enregistrer</button>
            <button onClick={cancel} className="btn-ghost"><X className="w-4 h-4" /> Annuler</button>
          </div>
        </div>
      )}

      {/* Mobile : une fiche par code promo (tout est visible en faisant defiler
          vers le BAS, plus besoin de glisser vers la droite). */}
      <div className="space-y-3 lg:hidden">
        {list.length === 0 && <p className="card p-8 text-center text-gray-500">Aucun code promo. Crée ton premier.</p>}
        {list.map((p) => (
          <div key={p.code} className="card p-4" data-testid={`admin-promo-card-${p.code}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono font-bold break-words">{p.code}</p>
                {p.description && <p className="text-xs text-gray-500">{p.description}</p>}
              </div>
              <button onClick={() => toggleActive(p)} className={`shrink-0 p-1 ${p.is_active ? "text-brand-green" : "text-gray-400"}`} title={p.is_active ? "Actif" : "Inactif"}>
                {p.is_active ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
              </button>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-gray-500">Remise</dt>
              <dd className="text-right font-semibold">{p.discount_type === "percent" ? `${p.discount_value}%` : `${p.discount_value}€`}</dd>
              <dt className="text-gray-500">Cible</dt>
              <dd className="text-right"><span className="badge gray">{p.applies_to === "all" ? "Tout" : p.applies_to === "packs" ? "Packs" : "Formations"}</span></dd>
              <dt className="text-gray-500">Utilisations</dt>
              <dd className="text-right font-semibold">{p.used_count || 0}{p.usage_limit ? ` / ${p.usage_limit}` : ""}</dd>
            </dl>
            <div className="mt-3 flex items-center gap-2 border-t border-emerald-100 pt-3">
              <button onClick={() => startEdit(p)} className="btn-ghost"><Pencil className="w-4 h-4" /> Éditer</button>
              <button onClick={() => remove(p.code)} className="ml-auto p-2 text-red-500 hover:text-red-700" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="card overflow-hidden hidden lg:block">
        <div data-testid="admin-promo-table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-emerald-50">
            <tr>
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Remise</th>
              <th className="text-left p-3">Cible</th>
              <th className="text-right p-3">Utilisations</th>
              <th className="text-center p-3">Actif</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">Aucun code promo. Crée ton premier.</td></tr>}
            {list.map((p) => (
              <tr key={p.code} className="border-t border-emerald-100" data-testid={`admin-promo-row-${p.code}`}>
                <td className="p-3"><div><p className="font-mono font-bold">{p.code}</p>{p.description && <p className="text-xs text-gray-500">{p.description}</p>}</div></td>
                <td className="p-3">{p.discount_type === "percent" ? `${p.discount_value}%` : `${p.discount_value}€`}</td>
                <td className="p-3"><span className="badge gray">{p.applies_to === "all" ? "Tout" : p.applies_to === "packs" ? "Packs" : "Formations"}</span></td>
                <td className="p-3 text-right">{p.used_count || 0}{p.usage_limit ? ` / ${p.usage_limit}` : ""}</td>
                <td className="p-3 text-center">
                  <button onClick={() => toggleActive(p)} className={p.is_active ? "text-brand-green" : "text-gray-400"} title={p.is_active ? "Actif" : "Inactif"} data-testid={`admin-promo-toggle-${p.code}`}>
                    {p.is_active ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
                  </button>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(p)} className="text-brand-green hover:text-emerald-700 mr-2" data-testid={`admin-edit-promo-${p.code}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.code)} className="text-red-500 hover:text-red-700" data-testid={`admin-delete-promo-${p.code}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
